import { useQuery } from '@apollo/client';
import { faHome } from '@fortawesome/free-solid-svg-icons/faHome';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, Container, Divider, FlexGrid, GoogleMapImage, Header, Image, Link, Loader, Modal, Panel, StarRating, Text, Visibility } from '@gasbuddy/react-components';
import { useToggle } from '@gasbuddy/react-hooks';
import classnames from 'classnames/bind';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Trans, useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import useAnalytics from '../../../lib/hooks/useAnalytics';
import useBannerAds from '../../../lib/hooks/useBannerAds';
import useConfig from '../../../lib/hooks/useConfig';
import useDelay from '../../../lib/hooks/useDelay';
import useDimensions from '../../../lib/hooks/useDimensions';
import useProfile from '../../../lib/hooks/useProfile';
import useSimpleWalletStatus from '../../../lib/hooks/useSimpleWalletStatus';
import { canonicalizeString, scrollToTop } from '../../../lib/utils';
import { generateBreadcrumbSchema } from '../../../lib/utils/generateBreadcrumbSchema';
import { generateStationSchema, getStationDescription } from '../../../lib/utils/generateStationSchema';
import getRegionByCodes from '../../../lib/utils/getRegionByCodes';
import { AD_SLOTS, AUSTRALIA_COUNTRY_CODE, DEFAULT_STATION_LOGO, FUEL_TYPES } from '../../constants';
import ANALYTICS_EVENTS from '../../constants/analyticsEvents';
import { REVIEWS_PAGE_SIZE } from '../../constants/reviewRatings';
import StationContext from '../../context/station';
import { Ad } from '../Ad';
import AmenitiesList from '../AmenitiesList';
import Breadcrumb from '../Breadcrumb';
import GasPriceCollection from '../GasPriceCollection';
import GenericStationList from '../GenericStationList';
import MobileDownloadAd from '../MobileDownloadAd';
import NoAustralia from '../NoAustralia';
import OutageForm from '../OutageForm';
import OutageSocialShare from '../OutageSocialShare';
import OutageStatus from '../OutageStatus';
import Page from '../Page';
import RegionAds from '../RegionAds';
import ReviewsContainer from '../Reviews';
import HelmetComponent from '../StationHelmet';
import StationInfoBox from '../StationInfoBox';
import StationPriceReport from '../StationPriceReport';
import StationStatusHistory from '../StationStatusHistory';
import SubmitReview from '../SubmitReview';
import TopSpottersList from '../TopSpottersList';
import { GET_STATION, GET_STATION_PRICES } from './Station.gql';
import styles from './Station.module.css';

const cx = classnames.bind(styles);

const buildMapsUrl = (station) => {
  if (station?.id) {
    const urlParts = [
      station.name,
      station.address.line1,
      station.address.locality,
      station.address.region,
      station.address.postalCode,
    ];
    const locationString = urlParts.map(val => val?.replace(' ', '+')).join(',');
    const coordinateString = `/@${station.latitude},${station.longitude},16z`;
    return `https://www.google.com/maps/dir//${locationString}${coordinateString}`;
  }
  return '';
};

export default function Station() {
  const { stationId } = useRouteMatch().params;
  const { consumerHost, googleMapsKey: mapKey, isMobile: isOnMobileDevice, recaptchaSiteKey } = useConfig();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [showStationMap, setShowStationMap] = useState(false);
  // const [mapDependenciesReady, setMapDependenciesReady] = useState(false);
  const { t, i18n } = useTranslation(['station', 'review', 'address', 'amenity', 'price', 'miscellaneous']);

  const analytics = useAnalytics();
  const { isTrusted } = useProfile();

  const walletStatus = useSimpleWalletStatus();
  const { hasPwGBCard, hasPwGBPlusCard, accountStatus: payAccountStatus } = walletStatus || {};

  // const handleBannerLoaded = useCallback(() => setMapDependenciesReady(true), []);

  const [showCash, toggleShowCash] = useToggle(false);
  useEffect(() => {
    if (showCash) {
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.TOGGLED_CASH_PRICES_ON,
      });
    }
  }, [analytics, showCash]);

  useEffect(() => {
    scrollToTop();
  }, []);

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { data, error: dataError, fetchMore, loading, refetch } = useQuery(GET_STATION, {
    variables: {
      id: stationId,
      lang: i18n.language,
    },
  });

  const {
    data: pricesData,
    loading: pricesLoading,
    refetch: refetchPrices,
  } = useQuery(GET_STATION_PRICES, {
    ssr: false,
    fetchPolicy: 'network-only',
    skip: !data?.station?.id,
    variables: { id: stationId },
  });

  const station = data?.station || {};
  const { amenities = [], fuels = [] } = station;

  const stationWithPrices = useMemo(() => {
    if (!station?.id) return station;
    const priceStation = pricesData?.station;
    const nearbyWithPrices = (station.nearby || []).map((nearbyStation) => {
      const nearbyPriceData = priceStation?.nearby?.find(n => n.id === nearbyStation.id);
      return {
        ...nearbyStation,
        prices: nearbyPriceData?.prices || [],
        offers: nearbyPriceData?.offers || null,
        payStatus: nearbyPriceData?.payStatus || null,
      };
    });
    return {
      ...station,
      prices: priceStation?.prices || [],
      offers: priceStation?.offers || null,
      payStatus: priceStation?.payStatus || null,
      nearby: nearbyWithPrices,
    };
  }, [station, pricesData]);
  const reviews = station.reviews?.results || [];

  if (!loading && !station?.id) {
    const error = new Error(dataError ? 'An error occurred while fetching station details' : 'Station could not be found');
    error.status = 404;
    throw error;
  }

  if (station.openStatus === 'closed_permanently') {
    const error = new Error('Station is permanently closed.');
    error.status = 410;
    throw error;
  }

  const googleMapsUrl = useMemo(() => buildMapsUrl(station), [station]);

  // const logGasBuddyCardBannerClickEvent = useCallback(() => {
  //   analytics.tagEvent({
  //     name: ANALYTICS_EVENTS.GASBUDDY_CARD_BANNER_CLICKED,
  //     attributes: { Location: 'StationDetails' },
  //   });
  // }, [analytics]);

  const handleReportPosted = useCallback(() => {
    analytics.identifyUser({ has_reported_prices: true });
    refetch();
    refetchPrices();
  }, [analytics, refetch, refetchPrices]);

  const numReviews = station.reviews?.count;
  const reviewPage = station.reviews?.page;
  const hasMoreReviews = useMemo(
    () => reviewPage < Math.ceil(numReviews / REVIEWS_PAGE_SIZE),
    [numReviews, reviewPage],
  );

  const loadMoreReviews = useCallback(() => {
    setIsLoadingMore(true);
    fetchMore({
      variables: {
        reviewsPage: reviewPage + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        setIsLoadingMore(false);
        if (!fetchMoreResult) {
          return prev;
        }
        return {
          station: {
            ...prev.station,
            reviews: {
              count: fetchMoreResult.station.reviews.count,
              page: fetchMoreResult.station.reviews.page,
              results: [
                ...prev.station.reviews.results,
                ...fetchMoreResult.station.reviews.results,
              ],
            },
          },
        };
      },
    });
  }, [fetchMore, reviewPage]);

  const markers = [
    {
      positions: [
        {
          lat: station?.latitude,
          lng: station?.longitude,
        },
      ],
      styles: {
        color: '0xee5340',
        icon: DEFAULT_STATION_LOGO,
      },
    },
  ];

  const { width: mapWidth, height: mapHeight, callbackRef: mapRef } = useDimensions();

  useEffect(() => {
    setHasInitialized(true);
  }, []);

  const { address, emergencyStatus: eStatus = {}, hasActiveOutage } = station;
  const dfpParams = useMemo(() => ({ station_id: stationId, station_brand: station.brandName }), [stationId, station.brandName]);
  const sellsDiesel = fuels.some(f => f === FUEL_TYPES.DIESEL.toLowerCase());
  const stationHasCashPrices = amenities?.some(amenity => amenity.amenityId === 'cash_credit');

  const mapConfigLoaded = !loading && mapKey;
  const showMap = useDelay({
    // begin: mapDependenciesReady && mapConfigLoaded && showStationMap, TODO: uncomment when we have new PwGB Banner
    begin: mapConfigLoaded && showStationMap,
    duration: 500,
  });

  const desktopBannerName = 'GasBuddy_SDP_728x90';
  const mobileBannerName = 'GasBuddy_MobileWeb_320x50_SDP';
  const bannerAds = useBannerAds(desktopBannerName, mobileBannerName);
  const pageMeta = useMemo(() => ({
    SMID: stationId,
    Brand: station.brandId,
    City: address?.locality,
    State: address?.region,
  }), [address, station.brandId, stationId]);

  const priceReportStation = useMemo(() => ({
    ...stationWithPrices,
    id: stationWithPrices?.id || stationId,
  }), [stationWithPrices, stationId]);

  const handleShowMap = useCallback(() => {
    setShowStationMap(true);
  }, []);

  const ratingsAndAmenities = useMemo(() => (
    <Fragment>
      <div className={cx('category')}>
        <Header as="h2" className={cx('header')}>
          {t('stationRatings', { ns: 'review' })}
        </Header>
        <Panel className={cx('rating')} color="white" bordered rounded>
          <Text bold className={cx('ratingAverage')} color="midnight" fluid>
            {station.starRating ? station.starRating.toFixed(1) : '---'}
          </Text>
          <div className={cx('ratingStars')}>
            <StarRating name="stationReviewsRating" rating={station.starRating} size="small" />
          </div>
          <Text fluid size="sm" color="midnight">
            {t('overallRating', { ns: 'review' })}
          </Text>
        </Panel>
        {hasInitialized && (
          <SubmitReview station={station} />
        )}
      </div>
      <div className={cx('category')}>
        <Header as="h2" className={cx('header')}>
          {t('featuresAmenities', { ns: 'station' })}
        </Header>
        <AmenitiesList amenities={station.amenities} />
      </div>
    </Fragment>
  ), [hasInitialized, station, t]);

  if (loading) {
    return (
      <Loader size="lg">
        {t('loadingStation', { ns: 'station' })}
      </Loader>
    );
  }

  if (!address?.locality || !address?.region) {
    return null;
  }
  const isAustraliaStation = address?.country === AUSTRALIA_COUNTRY_CODE;
  const stationDescription = getStationDescription(station, t);
  const stationSchema = generateStationSchema(station, t);

  const region = getRegionByCodes(address?.region, address?.country);
  const regionDisplayName = region?.name;
  const localityDisplayName = address?.locality;
  const breadcrumbOutline = [
    {
      linkTo: '/',
      text: t('navigation.home', { ns: 'miscellaneous' }),
    },
    {
      linkTo: '/home',
      text: t('navigation.gasPriceSearch', { ns: 'miscellaneous' }),
    },
  ];

  if (regionDisplayName) {
    breadcrumbOutline.push({
      linkTo: canonicalizeString(`/gasprices/${regionDisplayName}`),
      text: t(`regions.${address?.country}.${address?.region}`, { ns: 'address' }),
    });

    if (localityDisplayName) {
      breadcrumbOutline.push({
        linkTo: canonicalizeString(`/gasprices/${regionDisplayName}/${localityDisplayName}`),
        text: localityDisplayName.split(',')[0],
      });
    }
  }

  if (station) {
    breadcrumbOutline.push({
      linkTo: `/station/${station.id}`,
      text: `${station.name} (${address.line1})`,
    });
  }

  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbOutline);

  const breadCrumbNav = breadcrumbOutline.map(({ linkTo, text }, idx) => (
    <Breadcrumb
      key={linkTo || text}
      active={idx === breadcrumbOutline.length - 1}
      className={cx('breadcrumb')}
      linkTo={linkTo}
      showSeparator={idx > 0}
    >
      {idx === 0
        ? (
          <Fragment>
            <FontAwesomeIcon icon={faHome} />
            <Text>
              &nbsp;
              {text}
            </Text>
          </Fragment>
        )
        : text
      }
    </Breadcrumb>
  ));

  return (
    <Page
      name="Station_Details"
      meta={pageMeta}
      withTopAd
      adParams={dfpParams}
      bannerAds={bannerAds}
      breadcrumbSchema={breadcrumbSchema}
      description={stationDescription}
      schema={stationSchema}
      showAppBanner
    >
      {isAustraliaStation
        ? <NoAustralia />
        : (
          <StationContext.Provider value={stationId}>
            <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
              {station.name ? <HelmetComponent station={station} /> : null}
              <div className={cx('stationDetails')}>
                <Container>
                  <FlexGrid container padded>
                    <FlexGrid.Column className={cx('headingRow')}>
                      {!hasActiveOutage && isOnMobileDevice && (
                        <MobileDownloadAd analyticsScreenName="Details_Page" />
                      )}
                      {breadCrumbNav}
                      <Header>
                        {t('stationInLocalityRegion', {
                          ns: 'station',
                          stationName: station?.name,
                          locality: station?.address?.locality,
                          region: `(${station?.address?.line1})`,
                        })}
                      </Header>
                    </FlexGrid.Column>
                  </FlexGrid>
                  <FlexGrid container padded>
                    <FlexGrid.Column desktop={10}>
                      <FlexGrid container>
                        {hasActiveOutage && (
                          <FlexGrid.Column>
                            <Panel className={cx('outageWarningContainer')} color="orange">
                              <Image
                                className={cx('warningIcon')}
                                alt={t('icons.warning', { ns: 'miscellaneous' })}
                                fluid
                                lazy={false}
                                src="//static.gasbuddy.com/web/icons/warning-white.svg"
                              />
                              <Text bold>
                                {t('outage.mayBeOutages', { ns: 'station' })}
                              </Text>
                            </Panel>
                            <br />
                          </FlexGrid.Column>
                        )}
                        <FlexGrid.Column desktop={8}>
                          <StationInfoBox
                            station={stationWithPrices}
                            bordered
                            showAdditional
                            hasPwGBCard={hasPwGBCard}
                            hasPwGBPlusCard={hasPwGBPlusCard}
                            payAccountStatus={payAccountStatus}
                          />
                          <div className={cx('pricesHeaderContainer')}>
                            <Header as="h2" className={cx('header', 'pricesHeader')}>
                              {t('stationPrices', { ns: 'station' })}
                            </Header>
                            {stationHasCashPrices && (
                              <Checkbox
                                checked={showCash}
                                label={t('showCashPrices', { ns: 'price' })}
                                onChange={toggleShowCash}
                              />
                            )}
                          </div>
                          <GasPriceCollection
                            isLoadingPrices={pricesLoading}
                            showCashPrices={stationHasCashPrices && showCash}
                            station={stationWithPrices}
                          />
                          <StationPriceReport
                            onReportDone={handleReportPosted}
                            station={priceReportStation}
                          />
                          {/* <PwGBBanner
                            onBannerLoaded={handleBannerLoaded}
                            onClick={logGasBuddyCardBannerClickEvent}
                            station={station}
                          /> */}
                          {isTrusted && (
                            <div className={cx('stationEdits')}>
                              <Trans i18nKey="station:lookingToUpdate">
                                <Text>Looking to update station information?&nbsp;&nbsp;</Text>
                                <Visibility className={cx('hiddenBr')} desktop={false} tablet={false}>
                                  <br />
                                </Visibility>
                                <Link uppercase to={`/station-edits?id=${station.id}`}>Suggest Station Edits</Link>
                              </Trans>
                            </div>
                          )}
                        </FlexGrid.Column>
                        <FlexGrid.Column desktop={4}>
                          {
                            showStationMap ? (
                              <div className={cx('mapContainer')}>
                                <div className={cx('map')} ref={mapRef}>
                                  {
                                    showMap ? (
                                      <GoogleMapImage
                                        alt={station?.name}
                                        apiKey={mapKey}
                                        height={Math.ceil(mapHeight)}
                                        host={`//${consumerHost}/station/${stationId}/map`}
                                        markers={markers}
                                        width={Math.ceil(mapWidth)}
                                        zoom={16}
                                      />
                                    ) : <Loader>Loading map</Loader>
                                  }
                                </div>
                                <Button
                                  className={cx('directionsLink')}
                                  secondary
                                  fluid
                                  as="a"
                                  target="_blank"
                                  href={googleMapsUrl}
                                >
                                  {t('getDirections', { ns: 'station' })}
                                </Button>
                              </div>
                            ) : (
                              <Fragment>
                                <Button
                                  fluid
                                  secondary
                                  onClick={handleShowMap}
                                  className={cx('showMapButton')}
                                >
                                  {t('showStationMapLocation', { ns: 'station' })}
                                </Button>
                                {ratingsAndAmenities}
                              </Fragment>
                            )
                          }

                        </FlexGrid.Column>
                      </FlexGrid>
                      {hasActiveOutage && (
                        <FlexGrid container padded>
                          <FlexGrid.Column>
                            <Fragment>
                              <Header as="h2" className={cx('header')}>
                                {t('outage.stationStatus', { ns: 'station' })}
                              </Header>
                              <Panel
                                color="white"
                                padded={false}
                                bordered
                                rounded
                              >
                                <FlexGrid>
                                  <FlexGrid.Column>
                                    <OutageStatus
                                      powerReport={eStatus?.hasPower && {
                                        status: eStatus.hasPower.reportStatus,
                                        username: eStatus.hasPower.nickname || eStatus.hasPower.stamp,
                                        timestamp: eStatus.hasPower.updateDate,
                                      }}
                                      gasReport={eStatus?.hasGas && {
                                        status: eStatus.hasGas.reportStatus,
                                        username: eStatus.hasGas.nickname || eStatus.hasGas.stamp,
                                        timestamp: eStatus.hasGas.updateDate,
                                      }}
                                      dieselReport={(eStatus?.hasDiesel && {
                                        status: eStatus.hasDiesel.reportStatus,
                                        username: eStatus.hasDiesel.nickname || eStatus.hasDiesel.stamp,
                                        timestamp: eStatus.hasDiesel.updateDate,
                                      })}
                                      sellsDiesel={sellsDiesel}
                                    />
                                  </FlexGrid.Column>
                                  <FlexGrid.Column>
                                    <Panel className={cx('outageCTAs')} color="white" bordered={false}>
                                      <Modal
                                        content={({ close }) => (
                                          <OutageForm
                                            includeDiesel={sellsDiesel}
                                            onCancel={close}
                                            onComplete={close}
                                            station={station}
                                          />
                                        )}
                                        size="sm"
                                      >
                                        {({ open }) => (
                                          <Button
                                            className={cx('outageButton')}
                                            primary
                                            onClick={open}
                                          >
                                            {t('outage.reportFuelOrPower', { ns: 'station' })}
                                          </Button>
                                        )}
                                      </Modal>
                                      <Modal
                                        content={({ close }) => (
                                          <OutageSocialShare
                                            status={eStatus}
                                            onComplete={close}
                                            includeDiesel={sellsDiesel}
                                          />
                                        )}
                                        size="sm"
                                      >
                                        {({ open }) => (
                                          <Button
                                            className={cx('outageButton')}
                                            secondary
                                            onClick={open}
                                          >
                                            {t('outage.shareStationStatus', { ns: 'station' })}
                                          </Button>
                                        )}
                                      </Modal>
                                      <Modal
                                        content={({ close }) => (
                                          <Fragment>
                                            <StationStatusHistory
                                              includeDiesel={sellsDiesel}
                                              stationId={stationId}
                                            />
                                            <Divider />
                                            <Button primary onClick={close}>
                                              {t('actions.close', { ns: 'miscellaneous' })}
                                            </Button>
                                          </Fragment>
                                        )}
                                        size="sm"
                                      >
                                        {({ open }) => (
                                          <Button
                                            className={cx('outageButton')}
                                            secondary
                                            onClick={open}
                                          >
                                            {t('outage.stationStatusHistory', { ns: 'station' })}
                                          </Button>
                                        )}
                                      </Modal>
                                    </Panel>
                                  </FlexGrid.Column>
                                </FlexGrid>
                              </Panel>
                            </Fragment>
                          </FlexGrid.Column>
                        </FlexGrid>
                      )}
                      <FlexGrid className={cx('secondaryInfoWrapper')} container padded>
                        <FlexGrid.Column desktop={8}>
                          <div className={cx('category')}>
                            <Header as="h2" className={cx('header')}>
                              {t('reviews', { ns: 'review' })}
                            </Header>
                            <ReviewsContainer
                              hasMoreReviews={hasMoreReviews}
                              isFetching={loading || isLoadingMore}
                              loadMore={loadMoreReviews}
                              reviews={reviews}
                              station={station}
                            />
                          </div>
                          <Header as="h2" className={cx('header')}>
                            {t('stationsNearby', { ns: 'station' })}
                          </Header>
                          <Panel color="white" padded={!stationWithPrices.nearby.length} rounded>
                            {stationWithPrices.nearby.length ? (
                              <GenericStationList
                                hidePayBanner
                                isFullTabletWidth
                                isLoadingPrices={pricesLoading}
                                stations={stationWithPrices.nearby}
                                verbose
                              />
                            ) : (
                              <Text as="p">
                                {t('noNearbyStationsFound', { ns: 'station' })}
                              </Text>
                            )}
                          </Panel>
                        </FlexGrid.Column>
                        <FlexGrid.Column desktop={4}>
                          {showStationMap && ratingsAndAmenities}
                          <div className={cx('category')}>
                            <Header as="h2" className={cx('header')}>
                              {t('topSpotters', { ns: 'station' })}
                            </Header>
                            <TopSpottersList topSpotters={station.topSpotters} />
                          </div>
                          <Ad
                            id={AD_SLOTS[4].slotID}
                            path="GasBuddy_SDP_300x250"
                            sizes={AD_SLOTS[4].sizes}
                            collapseIfEmpty
                          />
                        </FlexGrid.Column>
                      </FlexGrid>
                    </FlexGrid.Column>
                    <FlexGrid.Column className={cx('adContainer')} desktop={2}>
                      <RegionAds
                        topAdName="GasBuddy_SDP_160x600_1"
                        middleAdName="GasBuddy_SDP_160x600_2"
                        bottomAdName="GasBuddy_SDP_160x600_3"
                      />
                    </FlexGrid.Column>
                  </FlexGrid>
                </Container>
              </div>
            </GoogleReCaptchaProvider>
          </StationContext.Provider>
        )}
    </Page>
  );
}
